// API functions for PDF editor feature
import { POST, GET, DELETE } from '@/shared/api/requests';

// ============================================
// Types based on backend serializers
// ============================================

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface PDFPage {
  id: string;
  pdf_file: string;
  page_number: number;
  width: number;
  height: number;
  rotation: number;
  dpi: number;
  preview_url: string | null;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface PDFFile {
  id: string;
  project: number;
  project_name: string;
  file: string;
  file_url: string;
  original_filename: string;
  page_count: number;
  status: 'uploaded' | 'processing' | 'ready' | 'error';
  uploaded_by: User;
  pages: PDFPage[];
  created_at: string;
  updated_at: string;
}

export interface PDFFileList {
  id: string;
  project: number;
  project_name: string;
  file_url: string;
  original_filename: string;
  page_count: number;
  status: 'uploaded' | 'processing' | 'ready' | 'error';
  uploaded_by: User;
  created_at: string;
  updated_at: string;
}

export interface LayerDocument {
  id: string;
  pdf_file: string;
  pdf_filename: string;
  version: number;
  layers: Layer[];
  created_by: User;
  created_at: string;
  last_modified: string;
  last_modified_by: User;
}

export interface Layer {
  id: string;
  layer_document: string;
  page_number: number;
  layer_type: 'shape' | 'path' | 'text' | 'annotation' | 'image' | 'hyperlink';
  data: Record<string, any>;
  z_index: number;
  visible: boolean;
  created_by?: User;
  created_at?: string;
  updated_at?: string;
}

// ============================================
// API Functions
// ============================================

/**
 * Upload a PDF file to the backend
 * @param file - The PDF file to upload
 * @param projectId - The project ID to associate the PDF with
 * @returns Promise with upload response
 */
export async function uploadPDF(file: File, projectId: number): Promise<PDFFile> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('project', projectId.toString());
  formData.append('original_filename', file.name);

  const response = await POST<FormData>('/drawings/pdf-files/', formData);
  return response.data;
}

/**
 * Get a specific PDF file from the backend
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with PDF file details
 */
export async function getPDF(pdfId: string): Promise<PDFFile> {
  const response = await GET<void>(`/drawings/pdf-files/${pdfId}/`);
  return response.data;
}

/**
 * Get all PDF files for a project
 * @param projectId - Optional project ID to filter by
 * @returns Promise with array of PDF files
 */
export async function listPDFs(projectId?: number): Promise<PDFFileList[]> {
  const endpoint = projectId 
    ? `/drawings/pdf-files/?project=${projectId}`
    : '/drawings/pdf-files/';
  
  const response = await GET<void>(endpoint);
  
  // Handle paginated response from DRF
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  
  return response.data as any;
}

/**
 * Get all PDFs for a specific project (alternative endpoint)
 * @param projectId - The project ID
 * @returns Promise with project PDFs
 */
export async function getProjectPDFs(projectId: number): Promise<{
  project_id: number;
  project_name: string;
  pdfs: PDFFileList[];
}> {
  const response = await GET<void>(`/drawings/projects/${projectId}/pdfs/`);
  return response.data;
}

/**
 * Delete a PDF file from the backend
 * @param pdfId - The UUID of the PDF file to delete
 * @returns Promise<void>
 */
export async function deletePDF(pdfId: string): Promise<void> {
  await DELETE<void>(`/drawings/pdf-files/${pdfId}/`);
}

/**
 * Get the layer document associated with a PDF
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with layer document details
 */
export async function getLayerDocument(pdfId: string): Promise<LayerDocument> {
  const response = await GET<void>(`/drawings/pdf-files/${pdfId}/layer_document/`);
  return response.data;
}

/**
 * Create a new layer
 * @param layerData - The layer data to create
 * @returns Promise with created layer
 */
export async function createLayer(layerData: Omit<Layer, 'id' | 'created_at' | 'updated_at' | 'created_by'>): Promise<Layer> {
  const response = await POST<typeof layerData>('/drawings/layers/', layerData);
  return response.data;
}

/**
 * Update an existing layer
 * @param layerId - The UUID of the layer
 * @param updates - Partial layer data to update
 * @returns Promise with updated layer
 */
export async function updateLayer(layerId: string, updates: Partial<Pick<Layer, 'data' | 'z_index' | 'visible'>>): Promise<Layer> {
  const response = await POST<typeof updates>(`/drawings/layers/${layerId}/`, updates);
  return response.data;
}

/**
 * Delete a layer
 * @param layerId - The UUID of the layer to delete
 * @returns Promise<void>
 */
export async function deleteLayer(layerId: string): Promise<void> {
  await DELETE<void>(`/drawings/layers/${layerId}/`);
}

/**
 * Get all layers for a layer document
 * @param layerDocumentId - The UUID of the layer document
 * @param pageNumber - Optional page number to filter by
 * @returns Promise with array of layers
 */
export async function listLayers(layerDocumentId: string, pageNumber?: number): Promise<Layer[]> {
  let endpoint = `/drawings/layers/?layer_document=${layerDocumentId}`;
  if (pageNumber !== undefined) {
    endpoint += `&page_number=${pageNumber}`;
  }
  
  const response = await GET<void>(endpoint);
  
  // Handle paginated response from DRF
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  
  return response.data as any;
}

/**
 * Trigger PDF page processing
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with processing status
 */
export async function processPDFPages(pdfId: string): Promise<{ status: string; message: string }> {
  const response = await POST<Record<string, never>>(`/drawings/pdf-files/${pdfId}/process_pages/`, {});
  return response.data;
}

/**
 * Request PDF export with layers flattened
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with export job status
 */
export async function requestPDFExport(pdfId: string): Promise<{
  status: string;
  message: string;
  pdf_file: string;
  version: number;
}> {
  const response = await POST<{ pdf_file: string }>('/drawings/exports/', { pdf_file: pdfId });
  return response.data;
}

/**
 * List all exports for a PDF
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with array of exports
 */
export async function listPDFExports(pdfId: string): Promise<any[]> {
  const response = await GET<void>(`/drawings/exports/?pdf_file=${pdfId}`);
  
  // Handle paginated response from DRF
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  
  return response.data as any;
}
